import React, { useState, useEffect, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import { Eye, FileDown, Edit3, ArrowLeft } from 'lucide-react';
import { supabase } from '../lib/supabase';
import '../styles/RFIEditor.css';

interface RFIEditorProps {
  rfiId: string | null;
  estimationId: string;
  projectName?: string;
  contractNumber?: string;
  onBack: () => void;
}

const RFIEditor: React.FC<RFIEditorProps> = ({ rfiId: propRfiId, estimationId, projectName, contractNumber, onBack }) => {
  const [content, setContent] = useState('');
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const [rfiNumber, setRfiNumber] = useState<number | null>(null);
  const [subject, setSubject] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);

  // Load RFI when rfiId changes
  useEffect(() => {
    if (propRfiId) {
      loadRFI(propRfiId);
    }
  }, [propRfiId]);

  const loadRFI = async (id: string) => {
    try {
      const { data: rfi, error } = await supabase
        .from('rfi_documents')
        .select('*')
        .eq('id', id)
        .single();

      if (error) throw error;

      setRfiNumber(rfi.rfi_number);
      setSubject(rfi.subject || '');
      setContent(rfi.content || getDefaultTemplate(rfi.rfi_number));
      console.log('✅ Loaded RFI:', rfi.rfi_number);
    } catch (error) {
      console.error('Error loading RFI:', error);
    }
  };

  const createNewRFI = async () => {
    try {
      // Get next RFI number
      const { data: nextNum } = await supabase
        .rpc('get_next_rfi_number', { p_estimation_id: estimationId });

      const newRFINumber = nextNum || 1;

      const { data: newRFI, error } = await supabase
        .from('rfi_documents')
        .insert({
          estimation_id: estimationId,
          rfi_number: newRFINumber,
          subject: 'New RFI',
          content: getDefaultTemplate(newRFINumber),
          status: 'draft'
        })
        .select()
        .single();

      if (error) throw error;

      console.log('✅ Created new RFI:', newRFINumber);
      return newRFI;
    } catch (error) {
      console.error('Error creating RFI:', error);
      return null;
    }
  };

  const getDefaultTemplate = (num: number) => {
    const today = new Date().toLocaleDateString('en-US');
    return `# RFI #${String(num).padStart(3, '0')}

**Project:** ${projectName || 'Project Name'}  
**Contract:** ${contractNumber || 'Contract Number'}  
**Date:** ${today}

---

## Subject
[Enter RFI subject here]

## Questions

### 1. [Question Title]
[Enter your question here]

---

**Requested Response Date:** [Date]  
**Priority:** Medium  
**Impact:** [Schedule/Cost/Design]
`;
  };

  // Auto-save with debounce
  useEffect(() => {
    if (!propRfiId || !content) return;

    const saveTimeout = setTimeout(() => {
      saveRFI();
    }, 3000); // Save 3 seconds after last edit

    return () => clearTimeout(saveTimeout);
  }, [content, subject, propRfiId]);

  const saveRFI = async () => {
    if (!propRfiId) return;

    setIsSaving(true);
    try {
      const { error } = await supabase
        .from('rfi_documents')
        .update({
          content,
          subject,
          updated_at: new Date().toISOString()
        })
        .eq('id', propRfiId);

      if (error) throw error;

      setLastSaved(new Date());
      console.log('💾 RFI auto-saved');
    } catch (error) {
      console.error('Error saving RFI:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleExportPDF = async () => {
    try {
      console.log('📄 Exporting RFI to PDF...');
      const { exportRFIToPDF } = await import('../utils/rfiExport');
      await exportRFIToPDF(content, rfiNumber || 1, projectName);
      console.log('✅ PDF exported successfully');
    } catch (error) {
      console.error('Error exporting PDF:', error);
      alert('Failed to export PDF. Please try again.');
    }
  };

  return (
    <div className="rfi-editor-container">
      {/* Header */}
      <div className="rfi-editor-header">
        <div className="rfi-header-left">
          <button className="rfi-back-btn" onClick={onBack} title="Back to RFI List">
            <ArrowLeft size={18} />
          </button>
          <h2 className="rfi-title">
            RFI #{String(rfiNumber || 1).padStart(3, '0')}
          </h2>
          {lastSaved && (
            <span className="rfi-save-status">
              {isSaving ? 'Saving...' : `Saved ${lastSaved.toLocaleTimeString()}`}
            </span>
          )}
        </div>
        <div className="rfi-header-right">
          <button
            className={`rfi-btn ${!isPreviewMode ? 'active' : ''}`}
            onClick={() => setIsPreviewMode(false)}
            title="Edit Mode"
          >
            <Edit3 size={16} />
            Edit
          </button>
          <button
            className={`rfi-btn ${isPreviewMode ? 'active' : ''}`}
            onClick={() => setIsPreviewMode(true)}
            title="Preview Mode"
          >
            <Eye size={16} />
            Preview
          </button>
          <button
            className="rfi-btn primary"
            onClick={handleExportPDF}
            title="Export to PDF"
          >
            <FileDown size={16} />
            Export PDF
          </button>
        </div>
      </div>

      {/* Editor/Preview Area */}
      <div className="rfi-editor-content">
        {!isPreviewMode ? (
          // Edit Mode - Plain Markdown Textarea
          <textarea
            className="rfi-markdown-editor"
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder="Enter RFI content in markdown..."
            spellCheck={true}
          />
        ) : (
          // Preview Mode - Formatted Markdown View
          <div className="rfi-markdown-preview">
            <ReactMarkdown>{content}</ReactMarkdown>
          </div>
        )}
      </div>
    </div>
  );
};

export default RFIEditor;

